import { useEffect, useState, type FormEvent } from "react";
import { useAuth, useProcedure, useQuery } from "highscale/react";
import type { AuthedUser } from "highscale";
import { Leagues, useLeagueRoute } from "./LeagueWorkspace";
import { Landing } from "./Landing";
import { Brand } from "./Brand";
import { HandleInput } from "./HandleInput";
import { at } from "./lexicons";

type Todo = at.tastetest.draft.list.DraftItem;

export function App() {
  const { user, authorizationStatus, reauthorize } = useAuth();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), 8000);
    return () => clearTimeout(timer);
  }, []);

  if (authorizationStatus === "loading") {
    return (
      <main className="app">
        <h1>Taste Test</h1>
        <p role="status">Checking your session…</p>
        {slow && (
          <p role="alert">
            Could not check your session. Check that Highscale is running, then{" "}
            <button onClick={() => window.location.reload()}>Retry</button>.
          </p>
        )}
      </main>
    );
  }
  if (authorizationStatus === "reauthorization-required") {
    return (
      <main className="app">
        <h1>Taste Test</h1>
        <p>Please sign in again to continue.</p>
        <button onClick={() => reauthorize()}>Sign in again</button>
      </main>
    );
  }
  if (!user)
    return (
      <>
        <Landing />
        <SignIn />
      </>
    );
  // Remount list state when the account changes. Logout reloads the page,
  // so the provider's private query cache is discarded as well.
  return <Checklist key={user.did} user={user} />;
}

function SignIn() {
  const { login } = useAuth();
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      // Platform-managed OAuth; the manifest requests identity only.
      await login(handle.trim() || undefined);
    } catch {
      setError("Could not start sign-in. Please try again.");
      setBusy(false);
    }
  }

  return (
    <section
      className="app auth-panel"
      id="signin"
      tabIndex={-1}
      aria-labelledby="signin-heading"
    >
      <Brand />
      <h2 id="signin-heading">Your next great pick starts here</h2>
      <p className="muted">Create a private league or join your friends</p>
      <form onSubmit={submit}>
        <label htmlFor="handle">Your ATProto handle</label>
        <div className="row">
          <HandleInput value={handle} onChange={setHandle} disabled={busy} />
          <button disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
        </div>
      </form>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
    </section>
  );
}

function Checklist({ user }: { user: AuthedUser }) {
  const { logout } = useAuth();
  const route = useLeagueRoute();
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const query = useQuery(at.tastetest.draft.list, {});
  const options = { invalidates: [at.tastetest.draft.list] };
  const create = useProcedure(at.tastetest.draft.create, options);
  const update = useProcedure(at.tastetest.draft.update, options);
  const remove = useProcedure(at.tastetest.draft.delete, options);

  // Every event awaits/catches its mutation. Keep drafts on failure and
  // serialize edits in this tab so stale updates don't race each other.
  async function run(action: () => Promise<unknown>, message: string) {
    setBusy(true);
    setError(undefined);
    try {
      await action();
      return true;
    } catch {
      setError(message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function add(event: FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    if (
      await run(
        () => create({ text }),
        "Could not add your item. Your text is still here; try again.",
      )
    )
      setDraft("");
  }

  return (
    <main className="app">
      <header>
        <div>
          <h1>Taste Test</h1>
          <p className="muted account">{user.handle ?? user.did}</p>
        </div>
        <button
          className="secondary"
          disabled={busy}
          onClick={() =>
            void run(() => logout(), "Could not sign out. Please try again.")
          }
        >
          Sign out
        </button>
      </header>
      <Leagues />
      {!route.startsWith("#league/") && route !== "#new-league" && (
        <details className="private-drafts">
          <summary>Private draft picks</summary>
          <form onSubmit={add}>
            <label htmlFor="draft">Save an idea for your next pick</label>
            <div className="row">
              <input
                id="draft"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={500}
                disabled={busy}
              />
              <button disabled={busy || !draft.trim()}>Add</button>
            </div>
          </form>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          {query.error && (
            <p className="error" role="alert">
              Could not load your draft picks.{" "}
              <button
                className="secondary"
                disabled={busy || query.isFetching}
                onClick={() =>
                  void run(
                    () => query.refetch(),
                    "Could not refresh your draft picks. Please try again.",
                  )
                }
              >
                Retry
              </button>
            </p>
          )}
          {query.isPending ? (
            <p className="muted" role="status">
              Loading your draft picks…
            </p>
          ) : query.data?.drafts.length === 0 ? (
            <p className="empty">Save your first draft pick.</p>
          ) : null}
          {query.data && (
            <ul className="drafts">
              {query.data.drafts.map((todo) => (
                <ChecklistItem
                  key={todo.id}
                  todo={todo}
                  busy={busy}
                  onToggle={() =>
                    run(
                      () => update({ id: todo.id, done: !todo.done }),
                      "Could not update your item. Please try again.",
                    )
                  }
                  onSave={(text) =>
                    run(
                      () => update({ id: todo.id, text }),
                      "Could not save your edit. Your text is still here; try again.",
                    )
                  }
                  onDelete={() =>
                    run(
                      () => remove({ id: todo.id }),
                      "Could not delete your item. Please try again.",
                    )
                  }
                />
              ))}
            </ul>
          )}
        </details>
      )}
    </main>
  );
}

function ChecklistItem({
  todo,
  busy,
  onToggle,
  onSave,
  onDelete,
}: {
  todo: Todo;
  busy: boolean;
  onToggle: () => Promise<boolean>;
  onSave: (text: string) => Promise<boolean>;
  onDelete: () => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(todo.text);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (await onSave(text.trim())) setEditing(false);
  }
  return (
    <li>
      {editing ? (
        <form onSubmit={save} className="edit">
          <label htmlFor={"edit-" + todo.id}>Edit draft pick</label>
          <input
            id={"edit-" + todo.id}
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={500}
            disabled={busy}
            autoFocus
          />
          <div className="actions">
            <button disabled={busy || !text.trim()}>Save</button>
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => setEditing(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <label className="item">
            <input
              type="checkbox"
              checked={todo.done}
              disabled={busy}
              onChange={() => void onToggle()}
            />
            <span className={todo.done ? "done" : ""}>{todo.text}</span>
          </label>
          <div className="actions">
            <button
              className="secondary"
              disabled={busy}
              onClick={() => {
                setText(todo.text);
                setEditing(true);
              }}
              aria-label={"Edit " + todo.text}
            >
              Edit
            </button>
            <button
              className="secondary"
              disabled={busy}
              onClick={() => void onDelete()}
              aria-label={"Delete " + todo.text}
            >
              Delete
            </button>
          </div>
        </>
      )}
    </li>
  );
}
